import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {Check, Circle} from 'views/icons';
import {translate} from 'views/module';

const StepProgressBar = (props) => {
  const {steps, activeIndex, stepStates} = props;

  const defaultSteps = [
    translate('form.FormProgressBar.CareOverview'),
    translate('form.FormProgressBar.ScheduleAndQuote'),
    translate('form.FormProgressBar.CarePlan'),
    translate('form.FormProgressBar.CaregiverMatching'),
  ];

  const stepLabels = steps && steps.length ? steps : defaultSteps;

  return (
    <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{flexWrap: 'wrap', rowGap: 1, justifyContent: 'center'}}
      >
        {stepLabels.map((label, index) => {
          const isActive = index === activeIndex;
          const status = stepStates?.[index] || 'unopened';
          const isCompleted = status === 'completed';
          const isOpened = status === 'opened';
          const isInProgress = isOpened || isActive;
          const connectorColor = isCompleted ? '#DA7955' : '#D6CFC9';
          const circleStyles = {
            width: 14,
            height: 14,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isCompleted ?
              '#188629' :
              isInProgress ?
                'linear-gradient(90deg, white 50%, #DA7955 50%)' :
                'white',
            border: isCompleted ?
              'none' :
              isInProgress ?
                '1px solid #DA7955' :
                '1px solid #CFC7C2',
          };

          return (
            <React.Fragment key={`${label}-${index}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: isActive ? '6px 12px' : 0,
                  borderRadius: '20px',
                  backgroundColor: isActive ? 'white' : 'transparent',
                  boxShadow: isActive ? '0 0 0 1px white inset' : 'none',
                  textAlign: 'left',
                }}
                aria-current={isActive ? 'step' : undefined}
              >
                <Box sx={circleStyles}>
                  {isCompleted && <Check sx={{fontSize: 10, color: 'white'}} />}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    color: '#2D2925',
                    lineHeight: '130%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              </Box>
              {
                index < stepLabels.length - 1 && (
                  <Box sx={{display: {xs: 'none', sm: 'flex'}, alignItems: 'center', gap: {xs: '3px', sm: '4px'}}}>
                    <Box
                      sx={{
                        height: '1px',
                        width: {xs: '8px', sm: '12px'},
                        borderBottom: '1px dashed',
                        borderColor: connectorColor,
                      }}
                    />
                    <Circle sx={{fontSize: '4px', color: connectorColor}} />
                    <Box
                      sx={{
                        height: '1px',
                        width: {xs: '8px', sm: '12px'},
                        borderBottom: '1px dashed',
                        borderColor: connectorColor,
                      }}
                    />
                  </Box>
                )
              }
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};

StepProgressBar.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string),
  activeIndex: PropTypes.number,
  stepStates: PropTypes.arrayOf(PropTypes.oneOf(['unopened', 'opened', 'completed'])),
};

export default React.memo(StepProgressBar);
